import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
// Api
import { REMOVE_TICKET_EVENT_PARTICIPANT } from 'api/ticketing/mutations';
// Types
import {
  RemoveTicketEventParticipant,
  RemoveTicketEventParticipantVariables,
} from 'api/ticketing/types/RemoveTicketEventParticipant';
// Helpers
import { runTicketingChange } from 'pages/Events/runTicketingChange';

type RemovableParticipant = { id: string; userId: string | null; name: string };

export type RemoveParticipant = {
  /** Resolves to whether they were removed; the affiliate lists refresh when they are. */
  removeParticipant: (participant: RemovableParticipant) => Promise<boolean>;
};

export const useRemoveParticipant = (
  ticketEventId: string
): RemoveParticipant => {
  const [removeMutation] = useMutation<
    RemoveTicketEventParticipant,
    RemoveTicketEventParticipantVariables
  >(REMOVE_TICKET_EVENT_PARTICIPANT, {
    refetchQueries: [
      'GetTicketEventAffiliates',
      'GetTicketEventAffiliateSummary',
    ],
  });

  const removeParticipant = useCallback(
    (participant: RemovableParticipant): Promise<boolean> =>
      runTicketingChange(
        () =>
          removeMutation({
            variables: {
              input: {
                ticketEventId,
                userId: participant.userId || participant.id,
              },
            },
          }),
        `${participant.name} was removed from the event`,
        'Could not remove the participant'
      ),
    [removeMutation, ticketEventId]
  );

  return { removeParticipant };
};
